import { Download, ScrollText } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import {
    exportAuditLogs,
    listAuditLogs,
    type ListAuditParams,
} from '~/api/audit'
import type { AuditLog } from '~/api/types'
import {
    actionLabel,
    actorLabel,
    shortId,
    targetTypeLabel,
} from '~/lib/audit-labels'
import { DataTable } from '~/components/DataTable'
import { PageHeader } from '~/components/PageHeader'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Skeleton } from '~/components/ui/skeleton'
import { useToast } from '~/components/ui/toast'

// 플랫폼 > 감사로그: 위험 작업(권한 변경·삭제 등)의 흔적을 읽기 전용으로 가시화한다.
// (DESIGN.md §5.1 /admin/audit)

const PAGE_SIZE = 25

// action 종류별 색. 삭제/제거는 위험 톤, 변경류는 경고/정보 톤.
function actionTone(action: string): BadgeTone {
    const a = action.toLowerCase()
    if (a.includes('delete') || a.includes('remove')) return 'danger'
    if (a.includes('create') || a.includes('add')) return 'success'
    if (a.includes('update') || a.includes('change') || a.includes('role'))
        return 'warning'
    return 'info'
}

// detail 객체를 짧게 요약한다. 키 일부를 "k: v" 형태로, 길면 잘라낸다.
function summarizeDetail(detail: Record<string, unknown> | null): string {
    if (!detail) return '-'
    const entries = Object.entries(detail)
    if (entries.length === 0) return '-'
    const text = entries
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${formatValue(v)}`)
        .join(', ')
    const more = entries.length > 3 ? ` …(+${entries.length - 3})` : ''
    return text + more
}

function formatValue(value: unknown): string {
    if (value == null) return '∅'
    if (typeof value === 'object') return JSON.stringify(value)
    const s = String(value)
    return s.length > 40 ? `${s.slice(0, 40)}…` : s
}

function formatDate(iso: string): string {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ko-KR')
}

export function AuditPage() {
    const toast = useToast()
    const [logs, setLogs] = useState<AuditLog[]>([])
    const [loading, setLoading] = useState(false)

    // 적용된 필터(쿼리 발사용)와 입력 중 텍스트를 분리한다.
    const [actor, setActor] = useState('')
    const [action, setAction] = useState('')
    const [actorInput, setActorInput] = useState('')
    const [actionInput, setActionInput] = useState('')
    const [offset, setOffset] = useState(0)
    const [downloading, setDownloading] = useState(false)

    useEffect(() => {
        const params: ListAuditParams = {
            actor: actor || undefined,
            action: action || undefined,
            limit: PAGE_SIZE,
            offset,
        }
        setLoading(true)
        listAuditLogs(params)
            .then(setLogs)
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setLoading(false))
    }, [actor, action, offset, toast])

    const onSearch = (e: FormEvent) => {
        e.preventDefault()
        setActor(actorInput.trim())
        setAction(actionInput.trim())
        setOffset(0)
    }

    // 현재 적용된 필터(actor·action)로 좁힌 감사 로그를 CSV로 내려받는다.
    const onExport = () => {
        setDownloading(true)
        exportAuditLogs({
            actor: actor || undefined,
            action: action || undefined,
        })
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setDownloading(false))
    }

    // 현재 페이지의 action별 분포(상위 6개). 위험 작업이 무엇이 많은지 한눈에.
    const actionTop = (() => {
        const counts = logs.reduce<Record<string, number>>((acc, l) => {
            acc[l.action] = (acc[l.action] ?? 0) + 1
            return acc
        }, {})
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
    })()

    return (
        <div>
            <PageHeader
                title='감사로그'
                description='시스템 감사 로그 (읽기 전용)'
            />

            <Card>
                <CardHeader className='flex-row flex-wrap items-end gap-4'>
                    <CardTitle className='mr-auto'>감사 기록</CardTitle>
                    <form
                        onSubmit={onSearch}
                        className='flex flex-wrap items-end gap-4'>
                        <div className='flex flex-col gap-1'>
                            <Label htmlFor='filter-actor'>행위자</Label>
                            <Input
                                id='filter-actor'
                                className='h-9 w-44'
                                value={actorInput}
                                onChange={(e) => setActorInput(e.target.value)}
                                placeholder='actor'
                            />
                        </div>
                        <div className='flex flex-col gap-1'>
                            <Label htmlFor='filter-action'>액션</Label>
                            <Input
                                id='filter-action'
                                className='h-9 w-44'
                                value={actionInput}
                                onChange={(e) => setActionInput(e.target.value)}
                                placeholder='action'
                            />
                        </div>
                        <Button type='submit' size='sm' className='h-9'>
                            검색
                        </Button>
                        <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            className='h-9'
                            disabled={downloading}
                            onClick={onExport}>
                            <Download />
                            {downloading ? '내려받는 중…' : 'CSV 다운로드'}
                        </Button>
                    </form>
                </CardHeader>
                <CardContent>
                    {actionTop.length > 0 ? (
                        <div className='mb-3 flex flex-wrap items-center gap-2'>
                            <span className='text-muted-foreground text-xs'>
                                이 페이지 액션 분포
                            </span>
                            {actionTop.map(([a, count]) => (
                                <Badge key={a} tone={actionTone(a)} title={a}>
                                    {actionLabel(a)} · {count}
                                </Badge>
                            ))}
                        </div>
                    ) : null}
                    {loading ? (
                        <div className='space-y-2'>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Skeleton key={i} className='h-9' />
                            ))}
                        </div>
                    ) : logs.length === 0 ? (
                        <EmptyState
                            icon={ScrollText}
                            title='감사 로그가 없어요.'
                            description={
                                actor || action
                                    ? '검색 조건에 맞는 기록이 없어요. 행위자·액션 필터를 조정해 보세요.'
                                    : '권한 변경·삭제 등 위험 작업이 발생하면 여기에 기록돼요.'
                            }
                        />
                    ) : (
                        <DataTable
                            columns={[
                                '시각',
                                '행위자',
                                '어디서(IP)',
                                '액션',
                                '대상 유형',
                                '대상 ID',
                                '상세',
                            ]}
                            rows={logs.map((log) => [
                                formatDate(log.createdAt),
                                <div
                                    key='actor'
                                    className='min-w-0'
                                    title={log.actor}>
                                    <div className='truncate'>
                                        {actorLabel(log)}
                                    </div>
                                    {log.actorName && log.actorEmail ? (
                                        <div className='text-muted-foreground truncate text-xs'>
                                            {log.actorEmail}
                                        </div>
                                    ) : null}
                                </div>,
                                <span
                                    key='ip'
                                    title={log.userAgent ?? undefined}
                                    className='text-muted-foreground font-mono text-xs'>
                                    {log.ip ?? '—'}
                                </span>,
                                <Badge
                                    key='action'
                                    tone={actionTone(log.action)}
                                    title={log.action}>
                                    {actionLabel(log.action)}
                                </Badge>,
                                targetTypeLabel(log.targetType),
                                <span
                                    key='target'
                                    title={log.targetId ?? undefined}
                                    className='text-muted-foreground font-mono text-xs'>
                                    {shortId(log.targetId) || '-'}
                                </span>,
                                <span
                                    key='detail'
                                    className='text-muted-foreground text-xs'>
                                    {summarizeDetail(log.detail)}
                                </span>,
                            ])}
                        />
                    )}
                    <div className='mt-4 flex items-center justify-between'>
                        <span className='text-muted-foreground text-sm'>
                            {logs.length === 0
                                ? '0건'
                                : `${offset + 1}–${offset + logs.length}번째`}
                        </span>
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={offset === 0 || loading}
                                onClick={() =>
                                    setOffset(Math.max(0, offset - PAGE_SIZE))
                                }>
                                이전
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={logs.length < PAGE_SIZE || loading}
                                onClick={() => setOffset(offset + PAGE_SIZE)}>
                                다음
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
